import type {
  IChangeMobileData,
  ICreateUserData,
  ICustomerIdResponse,
  IGetVerificationCodeParams,
  ILeaveUserData,
  IQueryUserListParams,
  IUpdateUserData,
  IUserInfoResponse,
  IUserLoginParams,
  IUserOperationLogRow,
  IUserRowData,
  IUserSmsLoginParams,
  IWarehouseOption,
} from "./model";
import type { IPaginatedRecords } from "@/shared/types/api";
import request from "@/shared/api/request";

const Api = {
  Login: "/saas/non-auth/account/login",
  PhoneLogin: "/saas/non-auth/phone/login",
  VerificationCode: "/saas/non-auth/verification/code",
  UserInfo: "/saas/user/get-login-user",
  ChangePassword: "/saas/user/change-password",
  CustomerId: "/platform/non-auth/customerId",
  WarehouseList: "/platform/non-auth/warehouse/list",
  User: "/saas/user/page",
  UserDetail: "/saas/user",
  UserCreate: "/saas/user/create",
  UserUpdate: "/saas/user/update",
  UserDelete: "/saas/user/delete",
  UserLeave: "/saas/user/leave",
  OperationLogs: "/saas/user/operation-logs",
  UserStatus: "/saas/user/status",
  ResetPassword: "/saas/user/reset-password",
  ChangeMobile: "/saas/user/change-mobile",
};

export function login(loginParams: IUserLoginParams) {
  return request.post<{ token: string }>(Api.Login, loginParams);
}

export function phoneLogin(loginParams: IUserSmsLoginParams) {
  return request.post<{ token: string }>(Api.PhoneLogin, loginParams);
}

export function getVerificationCode(params: IGetVerificationCodeParams) {
  return request.post<string>(Api.VerificationCode, params);
}

export function getUserInfo() {
  return request.get<IUserInfoResponse>(Api.UserInfo);
}

export function changePassword(password: string) {
  return request.post<void>(Api.ChangePassword, { password });
}

export function getCustomerIdBySubdomain(subdomain: string) {
  return request.get<ICustomerIdResponse>(`${Api.CustomerId}/${subdomain}`);
}

export function getWarehouseListByCustomerId(customerId: string) {
  return request.get<IWarehouseOption[]>(`${Api.WarehouseList}/${customerId}`);
}

export function getUserList(params: IQueryUserListParams) {
  return request.get<IPaginatedRecords<IUserRowData>>(Api.User, { params });
}

export function getUserDetail(userId: string) {
  return request.get<IUserRowData>(`${Api.UserDetail}/${userId}`);
}

export function createUser(userData: ICreateUserData) {
  return request.post<void>(Api.UserCreate, userData);
}

export function updateUser(userData: IUpdateUserData) {
  return request.put<void>(`${Api.UserUpdate}/${userData.id}`, userData);
}

export function deleteUser(userId: string) {
  return request.delete<void>(`${Api.UserDelete}/${userId}`);
}

export function leaveUser(data: ILeaveUserData) {
  return request.put<void>(`${Api.UserLeave}/${data.id}`, data);
}

export function getUserOperationLogs(userId: string) {
  return request.get<IPaginatedRecords<IUserOperationLogRow>>(Api.OperationLogs, {
    params: { id: userId },
  });
}

export function resetUserPassword(userId: string) {
  return request.put<string>(`${Api.ResetPassword}/${userId}`, undefined);
}

export function updateUserStatus(userId: string, status: "0" | "1") {
  return request.post<void>(Api.UserStatus, { id: userId, status });
}

export function forbidUser(userId: string) {
  return updateUserStatus(userId, "0");
}

export function openUser(userId: string) {
  return updateUserStatus(userId, "1");
}

export function changeMobile(data: IChangeMobileData) {
  return request.post<void>(Api.ChangeMobile, data);
}
